<div class="student-detail-page" *ngIf="!loading && !loadError && student">
  <!-- Top bar: context + breadcrumb -->
  <div class="detail-topbar">
    <div class="context-row">
      <mat-icon class="ctx-icon">filter_alt</mat-icon>
      <mat-form-field appearance="outline" class="ctx-select">
        <mat-label>Academic year</mat-label>
        <mat-select [value]="schoolContext.getAcademicYear()" (selectionChange)="schoolContext.setAcademicYear($event.value); onContextChange()">
          <mat-option *ngFor="let y of schoolContext.academicYears" [value]="y">{{ y }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" class="ctx-select">
        <mat-label>Term</mat-label>
        <mat-select [value]="schoolContext.getTermId()" (selectionChange)="schoolContext.setTermId($event.value); onContextChange()">
          <mat-option *ngFor="let t of schoolContext.terms" [value]="t.id">{{ t.label }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <nav class="breadcrumb" aria-label="Breadcrumb">
      <a routerLink="/home/dashboard">Home</a>
      <span class="sep">›</span>
      <a routerLink="/home/students">Students</a>
      <span class="sep">›</span>
      <span class="current">View student</span>
    </nav>
  </div>

  <div class="detail-layout">
    <!-- Profile rail -->
    <aside class="profile-rail">
      <div class="avatar-wrap">
        <div class="avatar" [style.backgroundImage]="student.photoUrl ? 'url(' + student.photoUrl + ')' : null">
          <mat-icon *ngIf="!student.photoUrl" class="avatar-placeholder">person</mat-icon>
        </div>
        <button type="button" class="camera-btn" mat-icon-button disabled matTooltip="Photo upload when API is ready" aria-label="Change photo">
          <mat-icon>photo_camera</mat-icon>
        </button>
      </div>
      <h2 class="student-name">{{ displayName(student) }}</h2>
      <div class="grade-wrap"><span class="grade-pill">{{ dash(student.className) }}</span></div>
      <div class="quick-actions">
        <button mat-mini-fab type="button" color="accent" disabled matTooltip="Messaging"><mat-icon>mail</mat-icon></button>
        <button mat-mini-fab type="button" color="primary" disabled matTooltip="Security"><mat-icon>shield</mat-icon></button>
        <button mat-mini-fab type="button" disabled matTooltip="Profile"><mat-icon>account_circle</mat-icon></button>
      </div>
      <div class="rail-heading">
        <mat-icon>school</mat-icon>
        Student details
      </div>
      <mat-nav-list dense class="section-nav">
        <mat-list-item
          *ngFor="let sec of sections"
          [class.active]="activeSection === sec.id"
          (click)="setSection(sec.id)"
        >
          <mat-icon matListIcon>{{ sec.icon }}</mat-icon>
          <span matLine>{{ sec.label }}</span>
        </mat-list-item>
      </mat-nav-list>
    </aside>

    <!-- Main -->
    <main class="detail-main">
      <mat-card class="content-card">
        <mat-card-title>
          <div class="content-title-row">
            <span>Student information</span>
            <span class="actions">
              <button mat-stroked-button color="primary" type="button" (click)="openEdit()">
                <mat-icon>edit</mat-icon>
                Edit information
              </button>
            </span>
          </div>
        </mat-card-title>

        <mat-card-content *ngIf="activeSection === 'general'">
            <h3 class="subsection-title">General information</h3>

            <h4 class="group-title">Identification</h4>
            <div class="field-grid">
              <div class="field">
                <span class="label">Student name</span>
                <span class="value">{{ formalName(student) }}</span>
              </div>
              <div class="field">
                <span class="label">Preferred / common name</span>
                <span class="value">{{ dash(student.preferredName) }}</span>
              </div>
              <div class="field">
                <span class="label">Previous / maiden name</span>
                <span class="value">{{ dash(student.previousName) }}</span>
              </div>
              <div class="field">
                <span class="label">Student ID</span>
                <span class="value">{{ dash(student.schoolStudentId) }}</span>
              </div>
              <div class="field">
                <span class="label">Alternate ID</span>
                <span class="value">{{ dash(student.alternateId) }}</span>
              </div>
              <div class="field">
                <span class="label">District ID</span>
                <span class="value">{{ dash(student.districtId) }}</span>
              </div>
              <div class="field">
                <span class="label">Admission number</span>
                <span class="value">{{ dash(student.admissionNumber) }}</span>
              </div>
              <div class="field">
                <span class="label">Roll number</span>
                <span class="value">{{ dash(student.rollNumber) }}</span>
              </div>
            </div>

            <h4 class="group-title">Demographic</h4>
            <div class="field-grid">
              <div class="field">
                <span class="label">Date of birth</span>
                <span class="value">{{ formatDate(student.dateOfBirth) }}</span>
              </div>
              <div class="field">
                <span class="label">Age</span>
                <span class="value">{{ ageLabel(student.dateOfBirth) }}</span>
              </div>
              <div class="field">
                <span class="label">Gender</span>
                <span class="value">{{ dash(student.gender) }}</span>
              </div>
              <div class="field">
                <span class="label">Race</span>
                <span class="value">{{ dash(student.race) }}</span>
              </div>
              <div class="field">
                <span class="label">Ethnicity</span>
                <span class="value">{{ dash(student.ethnicity) }}</span>
              </div>
              <div class="field">
                <span class="label">Country of birth</span>
                <span class="value">{{ dash(student.countryOfBirth) }}</span>
              </div>
              <div class="field">
                <span class="label">Nationality</span>
                <span class="value">{{ dash(student.nationality) }}</span>
              </div>
              <div class="field">
                <span class="label">First language</span>
                <span class="value">{{ dash(student.firstLanguage) }}</span>
              </div>
            </div>
        </mat-card-content>

        <mat-card-content *ngIf="activeSection === 'address' && student">
          <h3 class="subsection-title">Address &amp; contact</h3>

          <h4 class="group-title">Address</h4>
          <div class="field-grid">
            <div class="field">
              <span class="label">Door / plot / unit</span>
              <span class="value">{{ dash(student.addressDto?.doorNo) }}</span>
            </div>
            <div class="field">
              <span class="label">Landmark</span>
              <span class="value">{{ dash(student.addressDto?.landmark) }}</span>
            </div>
            <div class="field">
              <span class="label">Area / street</span>
              <span class="value">{{ dash(student.addressDto?.area) }}</span>
            </div>
            <div class="field">
              <span class="label">City</span>
              <span class="value">{{ dash(student.addressDto?.city) }}</span>
            </div>
            <div class="field">
              <span class="label">District</span>
              <span class="value">{{ dash(student.addressDto?.district) }}</span>
            </div>
            <div class="field">
              <span class="label">State</span>
              <span class="value">{{ dash(student.addressDto?.state) }}</span>
            </div>
            <div class="field">
              <span class="label">Country</span>
              <span class="value">{{ dash(student.addressDto?.country) }}</span>
            </div>
            <div class="field">
              <span class="label">PIN / postal code</span>
              <span class="value">{{ dash(student.addressDto?.pincode) }}</span>
            </div>
          </div>

          <h4 class="group-title">Contact</h4>
          <div class="field-grid">
            <div class="field">
              <span class="label">Mobile</span>
              <span class="value">{{ dash(student.mobileNumber) }}</span>
            </div>
            <div class="field">
              <span class="label">Email</span>
              <span class="value">{{ dash(student.email) }}</span>
            </div>
          </div>
        </mat-card-content>

        <mat-card-content *ngIf="activeSection === 'family' && student">
          <h3 class="subsection-title">Family information</h3>
          <div class="field-grid">
            <div class="field">
              <span class="label">Father name</span>
              <span class="value">{{ dash(student.fatherName) }}</span>
            </div>
            <div class="field">
              <span class="label">Mother name</span>
              <span class="value">{{ dash(student.motherName) }}</span>
            </div>
          </div>
        </mat-card-content>

        <mat-card-content *ngIf="activeSection === 'medical' && student">
          <h3 class="subsection-title">Medical information</h3>
          <div class="field-grid">
            <div class="field">
              <span class="label">Blood group</span>
              <span class="value">{{ dash(student.bloodGroup) }}</span>
            </div>
          </div>
          <p class="hint section-footnote">Allergies, medications, and doctor contacts can be added when the API provides them.</p>
        </mat-card-content>

        <mat-card-content *ngIf="activeSection === 'academic' && student">
          <h3 class="subsection-title">Academic information</h3>
          <p class="hint section-lead">
            Grades use the <strong>academic year</strong> selected above when the mark sheet includes a year; otherwise all sheets that list this student are shown.
            Use
            <a routerLink="/home/grades">Grades</a>,
            <a routerLink="/home/attendance">Attendance</a>, or
            <a routerLink="/home/accounts">Accounts</a>
            for day-to-day entry.
          </p>

          <h4 class="group-title">Course enrollments</h4>
          <p *ngIf="!enrollments.length" class="empty-hint">No course enrollments for this student yet.</p>
          <div *ngIf="enrollments.length" class="table-scroll">
            <table class="academic-table">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Status</th>
                  <th>Enrolled on</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let e of enrollments">
                  <td>{{ courseLabel(e) }}</td>
                  <td>{{ enrollmentStatusLabel(e.status) }}</td>
                  <td>{{ formatDate(e.enrollmentDate) }}</td>
                  <td>{{ dash(e.notes) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 class="group-title">Grades &amp; exams</h4>
          <p *ngIf="!gradeSummaries.length" class="empty-hint">No mark sheets include this student for the current filters.</p>
          <div *ngIf="gradeSummaries.length" class="table-scroll">
            <table class="academic-table">
              <thead>
                <tr>
                  <th>Exam / assessment</th>
                  <th>Class</th>
                  <th>Year</th>
                  <th>Score</th>
                  <th>%</th>
                  <th>Grade</th>
                  <th>Rank</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let g of gradeSummaries">
                  <td>{{ dash(g.examTitle) }}</td>
                  <td>{{ dash(g.className) }}</td>
                  <td>{{ dash(g.academicYear) }}</td>
                  <td>{{ g.obtained }} / {{ g.max }}</td>
                  <td>{{ formatPercent(g.percent) }}</td>
                  <td>{{ dash(g.letterGrade) }}</td>
                  <td>{{ g.rank }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 class="group-title">Fees</h4>
          <div *ngIf="!feeLedger" class="empty-hint">No fee ledger for this student. The backend should create a ledger row when the student is registered (or on first enrollment) and return it from the student-ledger API.</div>
          <ng-container *ngIf="feeLedger as L">
            <div class="field-grid fee-summary-grid">
              <div class="field">
                <span class="label">Expected (annual)</span>
                <span class="value">{{ formatCurrency(L.annualTotal) }}</span>
              </div>
              <div class="field">
                <span class="label">Total paid</span>
                <span class="value">{{ formatCurrency(L.totalPaid) }}</span>
              </div>
              <div class="field">
                <span class="label">Balance due</span>
                <span class="value">{{ formatCurrency(feeBalance()) }}</span>
              </div>
            </div>
            <p *ngIf="!L.payments?.length" class="empty-hint">No payments recorded.</p>
            <div *ngIf="L.payments?.length" class="table-scroll">
              <table class="academic-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let p of recentFeePayments()">
                    <td>{{ formatDate(p.date) }}</td>
                    <td>{{ formatCurrency(p.amount) }}</td>
                    <td>{{ dash(p.method) }}</td>
                    <td>{{ dash(p.remark) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>

          <h4 class="group-title">Attendance</h4>
          <div class="field-grid">
            <div class="field">
              <span class="label">Today ({{ dash(todayAttendanceDate) }})</span>
              <span class="value">{{ attendanceStatusLabel(todayAttendanceStatus) }}</span>
            </div>
            <div class="field">
              <span class="label">Class used for lookup</span>
              <span class="value">{{ dash(student.className) }}</span>
            </div>
          </div>
          <p class="hint section-footnote">
            Marks come from the attendance API for <strong>today’s date</strong> and this student’s class. For the full class grid, open
            <a routerLink="/home/attendance">Attendance</a>.
          </p>
        </mat-card-content>

        <mat-card-content *ngIf="isPlaceholderSection(activeSection)" class="placeholder-section">
            <mat-icon class="placeholder-icon">construction</mat-icon>
            <p><strong>{{ activeSectionLabel() }}</strong></p>
            <p class="hint">This section will load when the backend exposes the matching resource (e.g. documents).</p>
            <p class="hint">Use <strong>Edit information</strong> for fields available in the current student form.</p>
        </mat-card-content>
      </mat-card>
    </main>
  </div>
</div>

<div *ngIf="loading" class="state-center">
  <mat-spinner diameter="48"></mat-spinner>
  <p>Loading student…</p>
</div>

<div *ngIf="!loading && loadError" class="state-center error">
  <mat-icon color="warn">error_outline</mat-icon>
  <p>{{ loadError }}</p>
  <button mat-stroked-button color="primary" type="button" (click)="goBack()">Back to students</button>
</div>
